import { Divider, InputAdornment } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Search } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

import {
  CcfBox,
  CcfButton,
  CcfChatIcon,
  CcfDeboucedInput,
  CcfEmailIcon,
  CcfPhoneIcon,
  CcfSupervisorFilterIcon,
  CcfTypography,
  CcfVoiceMailIcon,
  CcfWorkItemIcon,
  CcfDirectionInIcon,
  CcfDirectionOutIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './ccf-acd-dashboard.module.scss';
import { useSelector } from 'react-redux';
import { selectAcdLeftNavExpanded } from '../../global.app.slice';
import { dashboardData } from './dashboardData';

/* eslint-disable-next-line */
export interface CcfAcdDashboardProps {}

const useStyles = makeStyles({
  root: {
    '& .MuiDataGrid-root': {
      border: 'none',
    },

    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: '0px',
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      textTransform: 'uppercase',
      color: '#526B79',
    },
    '& .MuiDataGrid-columnsContainer': {
      background: '#FFFFF',
      border: '1px solid #D2D8DB',
      boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.08)',
    },
    '& .styledrows': {
      height: '36px',
      background: '#FFFFF',
      border: '1px solid #D2D8DB',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '13px',
      lineHeight: '18px',

      color: '#333333',
    

      /* Inside auto layout */
    },
  },
  searchInput: {
    '&.MuiOutlinedInput-inputAdornedEnd': {
      paddingBottom: '7.5px',
    },
  },
});

declare type GridAlignment = 'left' | 'right' | 'center';
/**
 *Component used to display dashboard table on acd page
 * @param props - CcfAcdDashboardProps
 * @returns
 * @example
 *
 */
export function CcfAcdDashboard(props: CcfAcdDashboardProps) {
  const rowData = dashboardData;
  const [translate] = useTranslator();
  const expandedAcdNav = useSelector(selectAcdLeftNavExpanded);
  const classes = useStyles();
  /**
   * Function to handle drawer open close
   * @example renderIconForType()
   */
  const renderIconForType = (param: string) => {
    switch (param) {
      case 'email':
        return <CcfEmailIcon />;
      case 'phone':
        return <CcfPhoneIcon />;
      case 'chat':
        return <CcfChatIcon />;
      case 'voicemail':
        return <CcfVoiceMailIcon />;
      case 'workitem':
        return <CcfWorkItemIcon />;
      default:
        return '';
    }
  };

  /**
   * Function to handle drawer open close
   * @example renderIconForType()
   */
  const renderIconForInOut = (param: string) => {
    switch (param) {
      case 'IN':
        return <CcfDirectionInIcon />;
      case 'OUT':
        return <CcfDirectionOutIcon />;
      default:
        return '';
    }
  };

  const columns = [
    {
      field: 'type',
      headerName: 'TYPE',
      width: 110,
      //headerAlign: 'center' as GridAlignment,
      //align: 'center' as GridAlignment,
      headerClassName: 'gridHeaderText',
      renderCell: (params: any) => renderIconForType(params.value),
    },
    {
      field: 'direction',
      headerName: 'In/Out',
      width: 120,
      //headerAlign: 'center' as GridAlignment,
      //align: 'center' as GridAlignment,
      renderCell: (params: any) => renderIconForInOut(params.value),
    },
    {
      field: 'name',
      headerName: 'NAME',
      width: 160,
    },
    {
      field: 'campaign',
      headerName: 'CAMPAIGN',
      width: 150,
    },
    {
      field: 'disposition',
      headerName: 'DISP',
      width: 110,
      renderCell: (params: any) => (params.value === true ? 'Yes' : 'No'),
    },
    {
      field: 'autowrap',
      headerName: 'AUTO WRAP-UP',
      width: 150,
      renderCell: (params: any) => (params.value === true ? 'Yes' : 'No'),
    },
    {
      field: 'sla',
      headerName: 'SLA (THRESH/GOAL)',
      width: 160,
    },
    {
      field: 'status',
      headerName: 'STATUS',
      width: 160,
    },
  ];

  const rows = rowData;

  return (
    <CcfBox style={{ height: '91%', width:'95%' }}>
      <CcfBox className={styles.dashobardHeader}>
        <CcfBox className={styles.dashboardHeaderBlock}>
          <CcfTypography className={styles.txtAcdSkills}>
            {translate('acdSkills')}
          </CcfTypography>
          <CcfButton className={styles.btnNewSkills}>
            <CcfTypography className={styles.txtNewSkills}>
              {translate('newSkills')}
            </CcfTypography>
          </CcfButton>
        </CcfBox>
      </CcfBox>
      <CcfBox className={styles.tableBlock}>
        <CcfBox className={styles.tableHeader}>
          <CcfBox className={styles.tableHeaderTextBlock}>
            <CcfTypography className={styles.tableHeaderText}>
              1953 {translate('skills')}
            </CcfTypography>
            <Divider
              style={{ height: '20px', width: '1px', background: '#E5E5E5' }}
            />
            <CcfTypography className={styles.tableHeaderText}>
              {' '}
              0 {translate('selected')}
            </CcfTypography>
          </CcfBox>
          <CcfBox className={styles.searchBoxSection}>
            <CcfBox className={styles.searchBox}>
              <CcfDeboucedInput
                size="small"
                style={{
                  background: 'white',
                }}
                className={classes.searchInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search htmlColor="grey" />
                    </InputAdornment>
                  ),
                }}
                placeholder={translate('Starttypingtofindthing')}
                variant="outlined"
                fullWidth
                delay={1500}
              />
            </CcfBox>
          </CcfBox>
          <CcfBox className={styles.filterIconBlock}>
            <CcfSupervisorFilterIcon style={{ fontSize: 14 }} />
          </CcfBox>
        </CcfBox>
        <CcfBox
          className={classes.root}
          style={{
            height: '90%',
            backgroundColor: '#FFFF',
          }}
        >
          {' '}
          <DataGrid
            rows={rowData}
            columns={columns}
            pageSize={rows.length}
            rowHeight={36}
            headerHeight={44}
            checkboxSelection
            disableSelectionOnClick
            hideFooterPagination
            hideFooterSelectedRowCount
            hideFooter
            getRowClassName={(params: any) => `styledrows`}
            sx={{
              '& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
            }}
          />
        </CcfBox>
      </CcfBox>
    </CcfBox>
  );
}

export default CcfAcdDashboard;
